import styled from 'styled-components';
import { compareObjects } from '../../../functions/utils/comapareObjects';
import {
  LauncherStatesData,
  launcherStatesAllFalse
} from '../../../hooks/stores/launcherStatesStore';
import { UIData, useUIStore } from '../../../hooks/stores/uiStore';
import { StateViewInfo, stateViewsInfo } from '../../../info/stateViewsInfo';
import { VisibilityButton } from './VisibilityButton';
import { useScene } from '../../../context/sceneContext';

export const StateTaskItem = ({
  stateView,
  stateViewPaused,
  stateViewFailed,
  columnIndex,
}: {
  stateView: keyof typeof stateViewsInfo;
  stateViewPaused?: keyof typeof stateViewsInfo;
  stateViewFailed?: keyof typeof stateViewsInfo;
  columnIndex?: number;
}) => {
  const scene = useScene();
  const [UIData, setUIData] = useUIStore<UIData>();
  const useDirectVisibility = UIData.useDirectVisibility;

  const getViewData = (stateView: keyof typeof stateViewsInfo) => {
    switch (stateView) {
      case stateView:
        return stateViewsInfo[stateView] as StateViewInfo;
      case stateViewPaused:
        return stateViewPaused ? stateViewsInfo[stateViewPaused!] : ({} as StateViewInfo);
      case stateViewFailed:
        return stateViewFailed ? stateViewsInfo[stateViewFailed!] : ({} as StateViewInfo);
    }

    return stateViewsInfo[stateView];
  };

  const isActive = (stateView?: keyof typeof stateViewsInfo) => {
    if (stateView === undefined) return false;
    return scene.isViewActive(stateView);
  };

  const setActive = (stateView?: keyof typeof stateViewsInfo) => {
    if (stateView === undefined) return;
    scene.setLauncherView(stateView);
  };

  const isCurrentStateDifferentFromDisplayed = (stateView: keyof typeof stateViewsInfo) => {
    const stateViewData = getViewData(stateView);
    return (
      !compareObjects(UIData.currentLauncherState, stateViewData.significantLauncherState) &&
      UIData.selectedElementIDs.length > 0
    );
  };

  const computedVisibility = (stateView?: keyof typeof stateViewsInfo) => {
    if (stateView === undefined) return undefined;

    const stateViewData = getViewData(stateView);
    return scene.getSelectedElementsComputedVisibility(stateViewData.significantLauncherState, {
      ...launcherStatesAllFalse,
      ...stateViewData.launcherStates,
    });
  };
  const directVisibility = (stateView?: keyof typeof stateViewsInfo) => {
    if (stateView === undefined) return undefined;

    const stateViewData = getViewData(stateView);
    return scene.getSelectedElementsDirectVisibility(stateViewData.significantLauncherState);
  };

  const setHoveredLabel = (label?: string) => {
    if (!columnIndex) return;

    const newColumnLabelsHovered = [];
    newColumnLabelsHovered[columnIndex] = label;
    setUIData({
      stateViewControllerColumnLabelsHovered: newColumnLabelsHovered,
    });
  };

  const currentForState = (
    state: [keyof LauncherStatesData | null, string | null]
  ): [keyof LauncherStatesData | null, string | null] => {
    const stateViewData = getViewData(stateView);
    const viewDataPaused = getViewData(stateViewPaused!);
    const viewDataFailed = getViewData(stateViewFailed!);

    switch (state?.[0]) {
      case stateViewData.significantLauncherState[0]:
        return stateViewData.significantLauncherState;
      case viewDataPaused?.significantLauncherState[0]:
        return viewDataPaused.significantLauncherState;
      case viewDataFailed?.significantLauncherState[0]:
        return viewDataFailed.significantLauncherState;
    }

    return stateViewData.significantLauncherState;
  };

  const currentLauncherStates = (state: [keyof LauncherStatesData | null, string | null]) => {
    const stateViewData = getViewData(stateView);
    const viewDataPaused = getViewData(stateViewPaused!);
    const viewDataFailed = getViewData(stateViewFailed!);

    switch (state?.[0]) {
      case stateViewData.significantLauncherState[0]:
        return {
          ...launcherStatesAllFalse,
          ...stateViewData.launcherStates,
        };
      case viewDataPaused?.significantLauncherState[0]:
        return {
          ...launcherStatesAllFalse,
          ...viewDataPaused.launcherStates,
        };
      case viewDataFailed?.significantLauncherState[0]:
        return {
          ...launcherStatesAllFalse,
          ...viewDataFailed.launcherStates,
        };
    }

    return {
      ...launcherStatesAllFalse,
      ...stateViewData.launcherStates,
    };
  };

  return (
    <Container
      $isLeftContainerVisible={
        computedVisibility(stateView) === false || 
        (directVisibility(stateView) !== undefined && !useDirectVisibility)
      }
      $isRightContainerVisible={
        isActive(stateViewPaused) ||
        isActive(stateViewFailed) ||
        (UIData.selectedElementIDs.length > 0 &&
          (computedVisibility(stateViewPaused) === false ||
           computedVisibility(stateViewFailed) === false ||
           (!useDirectVisibility && (
             directVisibility(stateViewPaused) !== undefined ||
             directVisibility(stateViewFailed) !== undefined
           ))))
      }
    >
      <SubButtonsLeftContainer className="subButtonsLeft">
        <VisibilityButton
          forState={currentForState(UIData.currentLauncherState)}
          launcherStates={currentLauncherStates(UIData.currentLauncherState)}
        />
      </SubButtonsLeftContainer>

      <MainButton
        $isActive={isActive(stateView)}
        $isCurrentStateDifferentFromDisplayed={isCurrentStateDifferentFromDisplayed(stateView)}
        $computedVisibility={computedVisibility(stateView)}
        $useDirectVisibility={useDirectVisibility}
        onClick={() => {
          setActive(stateView);
        }}
        onMouseEnter={() => setHoveredLabel(getViewData(stateView).label)}
        onMouseLeave={() => setHoveredLabel(undefined)}
      >
        <i className={getViewData(stateView).iconClassName} style={{ fontSize: '24px', lineHeight: '24px' }} />
        <i className="icon-inprogress" style={{ translate: '0 -2px', fontSize: '12px', lineHeight: '12px' }} />
      </MainButton>

      <SubButtonsRightContainer className="subButtonsRight">
        {stateViewPaused && (
          <SubButton
            $isActive={isActive(stateViewPaused)}
            $isCurrentStateDifferentFromDisplayed={isCurrentStateDifferentFromDisplayed(stateViewPaused)}
            $computedVisibility={computedVisibility(stateViewPaused)}
            $useDirectVisibility={useDirectVisibility}
            onClick={() => {
              setActive(stateViewPaused);
            }}
            onMouseEnter={() => setHoveredLabel(getViewData(stateViewPaused).label)}
            onMouseLeave={() => setHoveredLabel(undefined)}
          >
            <i className="icon-paused" style={{ fontSize: '12px', lineHeight: '12px' }} />
          </SubButton>
        )}

        {stateViewFailed && (
          <SubButton
            $isActive={isActive(stateViewFailed)}
            $isCurrentStateDifferentFromDisplayed={isCurrentStateDifferentFromDisplayed(stateViewFailed)}
            $computedVisibility={computedVisibility(stateViewFailed)}
            $useDirectVisibility={useDirectVisibility}
            onClick={() => {
              setActive(stateViewFailed);
            }}
            onMouseEnter={() => setHoveredLabel(getViewData(stateViewFailed).label)}
            onMouseLeave={() => setHoveredLabel(undefined)}
          >
            <i className="icon-failed" style={{ fontSize: '12px', lineHeight: '12px' }} />
          </SubButton>
        )}
      </SubButtonsRightContainer>
    </Container>
  );
};

const Container = styled.div<{
  $isLeftContainerVisible?: boolean;
  $isRightContainerVisible?: boolean;
}>`
  position: relative;

  box-sizing: border-box;
  width: 50px;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;

  .subButtonsRight {
    transition: opacity var(--transition-2);
    opacity: 0;
  }

  .subButtonsLeft {
    transition: opacity var(--transition-2);
    opacity: 0;
  }

  &:hover {
    .subButtonsRight,
    .subButtonsLeft {
      opacity: 1;
    }
  }

  ${({ $isLeftContainerVisible }) =>
    $isLeftContainerVisible &&
    `
      .subButtonsLeft {
        opacity: 1;
      }
    `}

  ${({ $isRightContainerVisible }) =>
    $isRightContainerVisible &&
    `
      .subButtonsRight {
        opacity: 1;
      }
    `}
`;

const SubButtonsRightContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  translate: 100%;

  display: flex;
  flex-direction: column;
`;

const SubButtonsLeftContainer = styled.div`
  height: 100%;
  position: absolute;
  left: -1px;
  translate: -100%;

  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const MainButton = styled.div<{
  $isActive?: boolean;
  $isCurrentStateDifferentFromDisplayed?: boolean;
  $computedVisibility?: boolean;
  $useDirectVisibility?: boolean;
}>`
  --text-color: var(--main-2);
  --text-color-hovered: var(--main-1);
  --border-color: var(--neutral-3);

  ${({ $isActive, $isCurrentStateDifferentFromDisplayed }) =>
    $isActive &&
    `
      --border-color: var(${$isCurrentStateDifferentFromDisplayed ? '--primary-2' : '--primary-1'});
      --text-color: var(${$isCurrentStateDifferentFromDisplayed ? '--primary-2' : '--primary-1'});
      --text-color-hovered: var(--primary-1);
    `}

  ${({ $computedVisibility, $useDirectVisibility }) =>
    $computedVisibility === false &&
    `
    --text-color: var(--error-1);
    --text-color-hovered: var(--error-1);
  `}

  box-sizing: border-box;
  cursor: pointer;
  width: 50px;
  height: 50px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  color: var(--text-color);
  background-color: var(--neutral-2);
  border: 1px solid var(--border-color);
  border-radius: 50%;

  transition:
    background-color var(--transition-2),
    color var(--transition-2),
    border var(--transition-2);
  &:hover {
    background-color: var(--neutral-3);
    color: var(--text-color-hovered);
  }
`;

const SubButton = styled.div<{
  $isActive?: boolean;
  $isCurrentStateDifferentFromDisplayed?: boolean;
  $computedVisibility?: boolean;
  $useDirectVisibility?: boolean;
}>`
  --text-color: var(--main-2);
  --text-color-hovered: var(--main-1);
  --border-color: var(--neutral-3);

  ${({ $isActive, $isCurrentStateDifferentFromDisplayed }) =>
    $isActive &&
    `
      --border-color: var(${$isCurrentStateDifferentFromDisplayed ? '--primary-2' : '--primary-1'});
      --text-color: var(${$isCurrentStateDifferentFromDisplayed ? '--primary-2' : '--primary-1'});
      --text-color-hovered: var(--primary-1);
    `}

  ${({ $computedVisibility }) =>
    $computedVisibility === false &&
    `
      --text-color: var(--error-1);
      --text-color-hovered: var(--error-1);
    `}

  box-sizing: border-box;
  cursor: pointer;
  width: 25px;
  height: 25px;

  display: flex;
  justify-content: center;
  align-items: center;

  color: var(--text-color);
  background-color: var(--neutral-2);
  border: 1px solid var(--border-color);
  border-radius: 50%;

  transition:
    background-color var(--transition-2),
    color var(--transition-2),
    border var(--transition-2);
  &:hover {
    background-color: var(--neutral-3);
    color: var(--text-color-hovered);
  }
`;
